import { AnimatePresence, motion } from "framer-motion";
import { Clock, Music, Repeat, Search, X } from "lucide-react";
import React, { useState } from "react";
import { usePlayerStore } from "../../stores/playerStore";
import { Track } from "../../types";
import { formatTime } from "../../utils/formatTime";

interface TrackListInfoProps {
  tracks: Track[];
  title?: string;
  subtitle?: string;
  image?: string;
  showHeader?: boolean;
  onSearch?: (query: string) => void;
}

export const TrackListInfo: React.FC<TrackListInfoProps> = ({
  tracks,
  title,
  subtitle,
  image,
  showHeader = true,
  onSearch,
}) => {
  const { loopMode, setLoopMode } = usePlayerStore();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const totalDuration = tracks.reduce((acc, track) => acc + track.duration, 0);
  const uniqueArtists = new Set(tracks.map((track) => track.artist)).size;

  const getNextLoopMode = (current: typeof loopMode): typeof loopMode => {
    const modes: (typeof loopMode)[] = ["off", "one", "all"];
    const currentIndex = modes.indexOf(current);
    return modes[(currentIndex + 1) % modes.length];
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch?.(value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    onSearch?.("");
  };

  return (
    <div className="p-6 bg-gradient-to-b from-zinc-800/50 to-zinc-900/50">
      {showHeader && (
        <div className={`flex gap-6 mb-4 ${tracks.length <= 1 && "justify-center"}`}>
          {/* Album Art */}
          {image ? (
            <img src={image} alt={title} className="w-48 h-48 object-cover shadow-xl rounded-lg" />
          ) : (
            <div className="w-48 h-48 bg-zinc-800 flex items-center justify-center shadow-xl rounded-lg">
              <Music className="w-24 h-24 text-zinc-700" />
            </div>
          )}

          {/* Info Section */}
          {tracks.length > 1 && (
            <div className="flex flex-col justify-between flex-1">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-5xl font-bold">{title}</h1>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowSearch(!showSearch)}
                    className="p-2 hover:bg-zinc-800 rounded-full transition"
                  >
                    <Search className="w-6 h-6" />
                  </motion.button>
                </div>
                {subtitle && <p className="text-zinc-400 mb-2">{subtitle}</p>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <AnimatePresence>
          {showSearch && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="relative"
            >
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search in playlist..."
                className="w-full px-4 py-2 bg-zinc-800/50 rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-500 pl-10 pr-10"
                autoFocus
              />
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {searchQuery && <div className="text-sm text-yellow-500">Showing results for: "{searchQuery}"</div>}

        {tracks.length > 1 && (
          <div className="flex flex-wrap items-center gap-4 gap-y-2 text-sm text-zinc-400">
            <span>{tracks.length} tracks</span>
            {uniqueArtists > 1 && <span>•</span>}
            {uniqueArtists > 1 && <span>{uniqueArtists} artists</span>}
            <span>•</span>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatTime(totalDuration)}</span>
            </div>
            <span>•</span>
            <button
              onClick={() => setLoopMode(getNextLoopMode(loopMode))}
              className="group relative flex items-center gap-2 hover:text-white transition-colors"
              title={`Loop mode: ${loopMode}`}
            >
              <Repeat className={`w-4 h-4 ${loopMode !== "off" ? "text-yellow-500" : ""}`} />
              <span className="text-sm">
                {loopMode === "off" && "No repeat"}
                {loopMode === "one" && "Repeat one"}
                {loopMode === "all" && "Repeat all"}
              </span>
              {loopMode === "one" && (
                <span
                  className="absolute -top-1 left-3 text-[10px] font-bold text-yellow-500"
                  style={{
                    left: "0.12rem",
                    top: "0.2rem",
                    padding: "0 0.2rem",
                    height: "13px",
                    width: "13px",
                    lineHeight: "1.3",
                  }}
                >
                  1
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
